import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Button } from '@/components/ui/button';
import { Slider } from '@/components/ui/slider';
import { 
  Play, 
  Pause, 
  SkipBack, 
  SkipForward, 
  Volume2, 
  VolumeX,
  X,
  ExternalLink,
  Loader2,
  AlertCircle
} from 'lucide-react';

export const AudioPlayer = ({ 
  isOpen, 
  onClose,
  currentTrack,
  currentAlbum,
  isPlaying,
  isLoading,
  progress,
  duration,
  volume,
  error,
  onTogglePlayPause,
  onSeek,
  onVolumeChange,
  onNext,
  onPrevious,
}) => {
  const [localVolume, setLocalVolume] = useState([volume * 100]);
  const [isMuted, setIsMuted] = useState(false);
  const [visualizerBars, setVisualizerBars] = useState(Array(30).fill(0));

  useEffect(() => {
    setLocalVolume([volume * 100]);
  }, [volume]);

  useEffect(() => {
    let interval;
    if (isPlaying) {
      interval = setInterval(() => {
        setVisualizerBars(prev => 
          prev.map(() => Math.random() * 100)
        );
      }, 100);
    } else {
      setVisualizerBars(Array(30).fill(0));
    }
    return () => clearInterval(interval);
  }, [isPlaying]);

  const handleVolumeChange = (value) => {
    const newVolume = value[0] / 100;
    setLocalVolume(value);
    onVolumeChange(newVolume);
    if (newVolume > 0) setIsMuted(false);
  };

  const toggleMute = () => {
    if (isMuted) {
      onVolumeChange(localVolume[0] / 100);
      setIsMuted(false);
    } else {
      onVolumeChange(0);
      setIsMuted(true);
    }
  };

  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${String(secs).padStart(2, '0')}`;
  };

  // Get hyperfollow link for "Full Track" button (shows all platforms)
  const getHyperfollowLink = () => {
    return currentAlbum?.streamingLinks?.hyperfollow || 
           currentAlbum?.streamingLinks?.spotify || 
           null;
  };

  const currentTime = (progress / 100) * duration;
  const hyperfollowLink = getHyperfollowLink();

  if (!currentTrack && !isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ y: '100%' }}
          animate={{ y: 0 }}
          exit={{ y: '100%' }}
          transition={{ duration: 0.5, ease: [0.25, 0.46, 0.45, 0.94] }}
          className="fixed bottom-0 left-0 right-0 z-50 bg-charcoal/95 backdrop-blur-xl border-t border-crimson/20"
        >
          <div className="max-w-7xl mx-auto px-4 sm:px-6 py-3 sm:py-4">
            <div className="flex items-center gap-3 sm:gap-6">
              {/* Album Art */}
              {currentAlbum && (
                <div className="relative w-12 h-12 sm:w-16 sm:h-16 shrink-0 overflow-hidden">
                  <img
                    src={currentAlbum.coverArtUrl}
                    alt={currentAlbum.title}
                    className="w-full h-full object-cover"
                  />
                  <div className="absolute inset-0 border border-crimson/30" />
                  {isLoading && (
                    <div className="absolute inset-0 bg-background/60 flex items-center justify-center">
                      <Loader2 className="w-5 h-5 text-crimson animate-spin" />
                    </div>
                  )}
                </div>
              )}

              {/* Track Info */}
              <div className="flex-1 min-w-0">
                {error ? (
                  <div className="flex items-center gap-2 text-destructive">
                    <AlertCircle className="w-4 h-4" />
                    <span className="text-sm">{error}</span>
                  </div>
                ) : currentTrack ? (
                  <>
                    <div className="flex items-center gap-2 mb-0.5">
                      <span className="text-[10px] uppercase tracking-[0.2em] text-crimson font-body">
                        Preview
                      </span>
                      {!currentTrack.previewUrl && (
                        <span className="text-[10px] uppercase tracking-wider text-muted-foreground">
                          Coming Soon
                        </span>
                      )}
                    </div>
                    <h4 className="font-display text-base sm:text-lg text-ivory truncate">
                      {currentTrack.title}
                    </h4>
                    <p className="text-xs text-muted-foreground truncate">
                      {currentAlbum?.title}
                    </p>
                  </>
                ) : (
                  <p className="text-sm text-muted-foreground">Select a track to play</p>
                )}
              </div>

              {/* Visualizer (Desktop) */}
              <div className="hidden lg:flex items-end gap-[2px] h-10 px-4">
                {visualizerBars.map((height, i) => (
                  <motion.div
                    key={i}
                    className="w-1 waveform-bar"
                    animate={{ height: isPlaying ? `${Math.max(4, height * 0.35)}px` : '4px' }}
                    transition={{ duration: 0.08 }}
                  />
                ))}
              </div>

              {/* Controls */}
              <div className="flex items-center gap-2 sm:gap-3">
                <Button 
                  variant="ghost" 
                  size="iconSm" 
                  className="text-ivory hover:text-crimson hidden sm:flex"
                  onClick={onPrevious}
                  disabled={!currentTrack?.previewUrl}
                >
                  <SkipBack className="w-4 h-4" />
                </Button>
                
                <Button
                  variant="play"
                  size="icon"
                  onClick={onTogglePlayPause}
                  disabled={!currentTrack?.previewUrl || isLoading}
                >
                  {isLoading ? (
                    <Loader2 className="w-5 h-5 animate-spin" />
                  ) : isPlaying ? (
                    <Pause className="w-5 h-5" />
                  ) : (
                    <Play className="w-5 h-5 ml-0.5" />
                  )}
                </Button>
                
                <Button 
                  variant="ghost" 
                  size="iconSm" 
                  className="text-ivory hover:text-crimson hidden sm:flex"
                  onClick={onNext}
                  disabled={!currentTrack?.previewUrl}
                >
                  <SkipForward className="w-4 h-4" />
                </Button>
              </div>

              {/* Progress (Desktop) */}
              <div className="hidden md:flex items-center gap-3 flex-1 max-w-xs">
                <span className="text-xs text-muted-foreground font-body w-10 text-right">
                  {formatTime(currentTime)}
                </span>
                <Slider
                  value={[progress]}
                  onValueChange={(val) => onSeek(val[0])}
                  max={100}
                  step={0.1}
                  className="flex-1"
                  disabled={!currentTrack?.previewUrl}
                />
                <span className="text-xs text-muted-foreground font-body w-10">
                  {formatTime(duration)}
                </span>
              </div>

              {/* Volume (Desktop) */}
              <div className="hidden lg:flex items-center gap-2">
                <Button
                  variant="ghost"
                  size="iconSm"
                  onClick={toggleMute}
                  className="text-muted-foreground hover:text-crimson"
                >
                  {isMuted || localVolume[0] === 0 ? (
                    <VolumeX className="w-4 h-4" />
                  ) : (
                    <Volume2 className="w-4 h-4" />
                  )}
                </Button>
                <Slider
                  value={isMuted ? [0] : localVolume}
                  onValueChange={handleVolumeChange}
                  max={100}
                  className="w-20"
                />
              </div>

              {/* Full Track Link - Opens Hyperfollow (all platforms) */}
              {hyperfollowLink && (
                <a
                  href={hyperfollowLink}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="hidden sm:flex"
                >
                  <Button variant="neonOutline" size="sm">
                    <ExternalLink className="w-4 h-4 mr-2" />
                    Full Track
                  </Button>
                </a>
              )}

              {/* Close */}
              <Button
                variant="ghost"
                size="iconSm"
                onClick={onClose}
                className="text-muted-foreground hover:text-ivory"
              >
                <X className="w-4 h-4" />
              </Button>
            </div>

            {/* Mobile Progress */}
            <div className="md:hidden mt-3">
              <Slider
                value={[progress]}
                onValueChange={(val) => onSeek(val[0])}
                max={100}
                step={0.1}
                className="w-full"
                disabled={!currentTrack?.previewUrl}
              />
              <div className="flex justify-between mt-1">
                <span className="text-xs text-muted-foreground">{formatTime(currentTime)}</span>
                <span className="text-xs text-muted-foreground">{formatTime(duration)}</span>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
